import {
  FeatureToggleV2Type, formatKMStake, isXinStarCurrency, language,
  preciseDivide, preciseMultiply, soundManager, t, useChipSetStore, useFeatureToggleV2, useGameRuleStore,
  usePlayerStore, xinStarRtp,
} from '@slot-nx/core'
import { css } from '../../../styled-system/css'
import { vstack } from '../../../styled-system/patterns'

import boxB from '../../../asset/reactUi/gameRules/image_gameInfo_boxB.png'
import closeBtn from '../../../asset/reactUi/gameRules/image_gameInfo_close_normal.png'
import multiplier from '../../../asset/reactUi/gameRules/image_info01.png'
import lightPotBox from '../../../asset/reactUi/gameRules/image_info02.png'
import buyFeature from '../../../asset/reactUi/gameRules/image_info03.png'
import totalMultiplier from '../../../asset/reactUi/gameRules/image_info04.png'
import tutorial01 from '../../../asset/reactUi/gameRules/image_tutorial01.png'
import tutorial02 from '../../../asset/reactUi/gameRules/image_tutorial02.png'
import tutorial03 from '../../../asset/reactUi/gameRules/image_tutorial03.png'
import H1 from '../../../asset/reactUi/gameRules/H1.png'
import H2 from '../../../asset/reactUi/gameRules/H2.png'
import H3 from '../../../asset/reactUi/gameRules/H3.png'
import H4 from '../../../asset/reactUi/gameRules/H4.png'
import L1 from '../../../asset/reactUi/gameRules/L1.png'
import L2 from '../../../asset/reactUi/gameRules/L2.png'
import L3 from '../../../asset/reactUi/gameRules/L3.png'
import L4 from '../../../asset/reactUi/gameRules/L4.png'
import L5 from '../../../asset/reactUi/gameRules/L5.png'
import WW from '../../../asset/reactUi/gameRules/WILD.png'
import { GameSoundType } from '../../domain/sound/GameSoundType'
import { gameConfig } from '../../config'
import { GameSize } from './GameSize'

const rawPayTableData = {
  H1: [400, 40, 20],
  H2: [200, 20, 10],
  H3: [160, 16, 8],
  H4: [120, 12, 6],
  L1: [100, 10, 5],
  L2: [80, 8, 4],
  L3: [60, 6, 3],
  L4: [40, 4, 2],
  L5: [20, 2, 1],
  WW: [],
}
const betMultiplier = 20

const payTableData = Object.fromEntries(
  Object.entries(rawPayTableData).map(([key, values]) => [
    key,
    values.map(value => preciseDivide(value, betMultiplier)),
  ]),
) as typeof rawPayTableData

const symbolImages: Record<keyof typeof payTableData, string> = {
  H1: H1,
  H2: H2,
  H3: H3,
  H4: H4,
  L1: L1,
  L2: L2,
  L3: L3,
  L4: L4,
  L5: L5,
  WW: WW,
}

const payTableSymbols: { symbol: keyof typeof payTableData, isLast: boolean }[] = [
  { symbol: 'H1', isLast: false },
  { symbol: 'H2', isLast: false },
  { symbol: 'H3', isLast: false },
  { symbol: 'H4', isLast: false },
  { symbol: 'L1', isLast: false },
  { symbol: 'L2', isLast: false },
  { symbol: 'L3', isLast: false },
  { symbol: 'L4', isLast: false },
  { symbol: 'L5', isLast: false },
  { symbol: 'WW', isLast: true },
]

/** 各語系 RTP 文案中揭露的數值，必須與 locale 字串完全一致（含小數點符號） */
const gameRtp = '95.96'

export const GameRule = () => {
  const isShowGameRule = useGameRuleStore(state => state.isOpened)
  const enableBuyBonus = useFeatureToggleV2(FeatureToggleV2Type.enableBuyBonus)
  const displayRtp = useFeatureToggleV2(FeatureToggleV2Type.displayRTPGameInfo)
  const enableBetHistory = useFeatureToggleV2(FeatureToggleV2Type.enableBetHistory)
  const [currentStake] = useChipSetStore(x => [x.computed.currentStake])
  const currency = usePlayerStore(x => x.currency)
  const isFromXinStar = isXinStarCurrency(currency)
  // VI_VN 的小數點是逗號，直接 replace('95.96') 會找不到而靜默失效
  const isCommaDecimal = language === 'VI_VN'
  const gameRtpText = isCommaDecimal ? gameRtp.replace('.', ',') : gameRtp
  const xinStarRtpText = isCommaDecimal ? xinStarRtp.replace('.', ',') : xinStarRtp
  const replaceRtp = (text: string) => isFromXinStar ? text.replace(gameRtpText, xinStarRtpText) : text

  if (!isShowGameRule) return null

  return (
    <GameSize element={document.getElementById('root') as HTMLElement} resolution={gameConfig.resolution}>
      <div className={`backdrop ${css({ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0, 0, 0, 1)' })}`}>
        <div className={`container ${css({ position: 'relative', top: 0, left: 0, width: '100%', height: '100%' })}`}>
          <div className={`panel ${css({ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '1035px', height: 'calc(100% - 24px - 24px)', border: '3px solid #C1C1C1', borderRadius: '20px', alignItems: 'center', justifyContent: 'center' })}`}>
            <CloseButton />
            <div className={`page-title ${css({ padding: '50px 0' })}`} />
            <div className={`scroll-container ${css({
              'position': 'relative',
              'top': 0,
              'left': 0,
              'width': '1020px',
              'height': '1750px',
              'overflow': 'auto',
              'padding': '30px 0',
              '&::-webkit-scrollbar': {
                width: '16px',
                appearance: 'none',
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '4px',
                backgroundColor: '#4D4D4D',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#202020',
              },
            })}`}
            >
              <div className={`content ${vstack({ gap: '100px', alignItems: 'center' })}`}>
                {language === 'ZH_TW' && (
                  <Section>
                    <SectionDesc>本說明頁賠付表投注額變動會自動更新賠付表顯示獎金</SectionDesc>
                  </Section>
                )}
                <Section>
                  <SectionTitle text={t('game_rules_title_1')} />
                  <SectionDesc align="left">
                    { t('game_rules_1')}
                  </SectionDesc>
                  <div className={css({ display: 'flex', justifyContent: 'center', gap: '22.5px', flexWrap: 'wrap', marginTop: '30px' })}>
                    {payTableSymbols.map(item => (
                      <PayTableBox
                        key={item.symbol}
                        img={symbolImages[item.symbol]}
                        stake={currentStake}
                        symbol={item.symbol}
                        isLast={item.isLast}
                      />
                    ))}
                  </div>
                </Section>
                <Section>
                  <SectionTitle text={t('game_rules_title_2')} />
                  <SectionDesc>
                    {t('game_rules_3')}
                    <br />
                    <br />
                    {t('game_rules_4')}
                  </SectionDesc>
                </Section>
                <Section>
                  <SectionTitle text={t('game_rules_title_3')} />
                  <img src={multiplier} className={css({ marginBottom: '40px' })} alt="bonusGameImage" />
                  <SectionDesc>
                    {t('game_rules_5')}
                    <br />
                    <br />
                    {t('game_rules_6')}
                    <br />
                    <br />
                    {t('game_rules_7')}
                    <br />
                    <br />
                    {t('game_rules_8')}
                    <br />
                    <br />
                    {t('game_rules_9')}
                    <br />
                    <br />
                    {t('game_rules_10')}
                    <br />
                    <br />
                    {t('game_rules_11')}
                  </SectionDesc>
                </Section>
                <Section>
                  <SectionTitle text={t('game_rules_title_4')} />
                  <img src={lightPotBox} className={css({ margin: '40px 0' })} alt="mechanicImage" />
                  <SectionDesc>
                    {t('game_rules_12')}
                    <br />
                  </SectionDesc>
                </Section>
                <Section>
                  <SectionTitle text={t('game_rules_title_5')} />
                  <SectionDesc>
                    {t('game_rules_13')}
                    <br />
                  </SectionDesc>
                  <div className={css({ display: 'flex', justifyContent: 'space-between' })}>
                    <img src={tutorial01} className={css({ width: '400px' })} alt="mechanicImage" />
                    <div className={`section-desc ${css({ color: 'rgba(255, 255, 255, 1)', display: 'flex', flexDirection: 'column', justifyContent: 'center', fontSize: '36px', maxWidth: '900px', lineHeight: '37px' })}`}>
                      <div className={`section-title ${css({ textAlign: 'center', color: '#ECC350', fontSize: '48px', fontWeight: 'bold', marginBottom: '30px' })}`}>
                        {t('game_rules_14')}
                      </div>
                      {t('game_rules_15')}
                    </div>
                  </div>
                  <div className={css({ display: 'flex', justifyContent: 'space-between' })}>
                    <img src={tutorial02} className={css({ width: '400px' })} alt="mechanicImage" />
                    <div className={`section-desc ${css({ color: 'rgba(255, 255, 255, 1)', display: 'flex', flexDirection: 'column', justifyContent: 'center', fontSize: '36px', maxWidth: '900px', lineHeight: '37px' })}`}>
                      <div className={`section-title ${css({ textAlign: 'center', color: '#ECC350', fontSize: '48px', fontWeight: 'bold', marginBottom: '30px' })}`}>
                        {t('game_rules_16')}
                      </div>
                      {t('game_rules_17')}
                    </div>
                  </div>
                  <div className={css({ display: 'flex', justifyContent: 'space-between' })}>
                    <img src={tutorial03} className={css({ width: '400px' })} alt="mechanicImage" />
                    <div className={`section-desc ${css({ color: 'rgba(255, 255, 255, 1)', display: 'flex', flexDirection: 'column', justifyContent: 'center', fontSize: '36px', maxWidth: '900px', lineHeight: '37px' })}`}>
                      <div className={`section-title ${css({ textAlign: 'center', color: '#ECC350', fontSize: '48px', fontWeight: 'bold', marginBottom: '30px' })}`}>
                        {t('game_rules_18')}
                      </div>
                      {t('game_rules_19')}
                    </div>
                  </div>
                  <div className={css({ display: 'flex', justifyContent: 'space-between' })}>
                    <img src={totalMultiplier} className={css({ width: '400px' })} alt="mechanicImage" />
                    <div className={`section-desc ${css({ color: 'rgba(255, 255, 255, 1)', display: 'flex', flexDirection: 'column', justifyContent: 'center', fontSize: '36px', maxWidth: '900px', lineHeight: '37px' })}`}>
                      <div className={`section-title ${css({ textAlign: 'center', color: '#ECC350', fontSize: '48px', fontWeight: 'bold', marginBottom: '30px' })}`}>
                        {t('game_rules_title_7')}
                      </div>
                      {t('game_rules_21')}
                    </div>
                  </div>
                </Section>
                {enableBuyBonus && (
                  <Section>
                    <SectionTitle text={t('game_rules_title_6')} />
                    <img src={buyFeature} className={css({ margin: '30px 0px' })} alt="bonus" />
                    <SectionDesc>
                      {t('game_rules_20')}
                    </SectionDesc>
                  </Section>
                )}
                {enableBetHistory && (
                  <Section>
                    <SectionTitle text={t('game_rules_title_betHistory')} />
                    <SectionDesc>
                      {t('game_rules_betHistory')}
                    </SectionDesc>
                  </Section>
                )}

                {/*
                    rtp_info_1/2 才是 RTP 揭露本身，受 displayRTPGameInfo 控制；
                    其中 rtp_info_2 講的是購買功能的 RTP，另外再受 enableBuyBonus 控制。
                    rtp_info_3 是故障免責聲明，與 RTP 揭露無關，一律顯示。
                  */}
                {displayRtp && (
                  <Section>
                    <SectionTitle text={t('rtp_title')} />
                    <SectionDesc>
                      {replaceRtp(t('rtp_info_1'))}
                      {enableBuyBonus && (
                        <>
                          <br />
                          {replaceRtp(t('rtp_info_2'))}
                        </>
                      )}
                    </SectionDesc>
                  </Section>
                )}
                <Section>
                  <SectionHighLight text={t('rtp_info_3')} />
                </Section>

              </div>
              <div style={{
                textAlign: 'center', marginTop: '80px', color: 'white', fontSize: '40px' }}
              >
                V1.2.0
              </div>
            </div>
          </div>
        </div>
      </div>
    </GameSize>
  )
}
function CloseButton() {
  return (
    <div
      style={{ '--normal': `url(${closeBtn}) no-repeat center center / 100% 100%` } as React.CSSProperties}
      className={css({ 'position': 'absolute', 'top': '-18px', 'right': '-18px', 'width': '130px', 'height': '130px', 'display': 'flex', 'alignItems': 'center', 'justifyContent': 'center', 'cursor': 'pointer', '& > div': { background: 'var(--normal)' } })}
      onClick={() => {
        useGameRuleStore.setState({ isOpened: false })
        soundManager.play(GameSoundType.buttonClose)
      }}
    >
      <div className={css({ position: 'relative', width: '70px', height: '70px' })} />
    </div>
  )
}

function Section({ children }: { children: React.ReactNode }) {
  return (
    <div className={`section ${vstack({ gap: '0px', maxWidth: '940px', position: 'relative' })}`}>
      {children}
    </div>
  )
}

function SectionTitle({ text }: { text: string }) {
  return (
    <div className={`section-title ${css({ textAlign: 'center', color: '#ECC350', fontSize: '60px', fontWeight: 'bold', marginBottom: '30px' })}`}>
      {text}
    </div>
  )
}

function SectionDesc({ children, align = 'center' }: { children: React.ReactNode, align?: 'left' | 'center' | 'right' }) {
  return (
    <div className={`section-desc ${css({ color: 'rgba(255, 255, 255, 1)', textAlign: align, fontSize: '35px', maxWidth: '900px', lineHeight: '37px' })}`}>
      {children}
    </div>
  )
}

function SectionHighLight({ text }: { text: string }) {
  return (
    <div className={`section-highLight ${css({ color: '#00E9E9', fontSize: '28px', fontWeight: 'bold', marginTop: '40px', maxWidth: '730px' })}`}>
      {text}
    </div>
  )
}

function PayTableBox({ img, symbol, stake, isLast }: { img: string, symbol: keyof typeof payTableData, stake: number, isLast: boolean }) {
  const payTableList = [
    { label: '12~30', value: payTableData[symbol][0] },
    { label: '10~11', value: payTableData[symbol][1] },
    { label: '8~9', value: payTableData[symbol][2] },
  ]

  if (isLast) {
    return (
      <div className={css({ position: 'relative', display: 'flex', justifyContent: 'center' })}>
        <img src={boxB} alt="boxB" />
        <div className={css({ position: 'absolute', top: '0px', display: 'flex', alignItems: 'center', justifyContent: 'space-around', width: '100%', height: '100%' })}>
          <img src={img} className={css({ scale: '1.3', marginBottom: '20px' })} alt="symbolImg" />
          <span className={css({ color: '#ECC350', fontSize: '30px', fontWeight: 'bold', textAlign: 'center', width: '240px', wordBreak: 'break-word', whiteSpace: 'normal', display: 'inline-block' })}>
            {t('game_rules_2')}
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className={css({ position: 'relative', display: 'flex', justifyContent: 'center' })}>
      <img src={boxB} alt="boxB" />
      <div className={css({ position: 'absolute', top: '0px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' })}>
        <img src={img} className={css({ scale: '1.3' })} alt="symbolImg" />
        <ul className={css({ fontWeight: 'bold', textAlign: 'center', fontSize: '36px', lineHeight: '37px', listStyle: 'none', padding: 0, margin: 0 })}>
          {payTableList.map(({ label, value }, index) => (
            <li key={`${label}-${value}-${index}`} className={css({ padding: '12.5px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '250px', borderBottom: index < payTableList.length - 1 ? '1px solid rgba(255,255,255,0.5)' : 'none' })}>
              <span className={css({ color: '#fff', width: '130px' })}>{label}</span>
              <div className={css({ color: '#ECC350', flex: 1, display: 'flex', justifyContent: 'center' })}>
                {formatKMStake(preciseMultiply(stake, value))}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
